import React from 'react';
import { AdminSettings, Role } from '../../types';
import { AdminField, PolicyToggle } from './Fields';

type ScheduledSopTabProps = {
  settings: AdminSettings;
  setSettings: (s: AdminSettings) => void;
  saveSettings: (s: AdminSettings) => void;
};

const ROLE_OPTIONS: Role[] = ['RESERVATIONS', 'FLIGHT_PLANNER', 'DFO', 'CP', 'PILOT', 'FIRST_OFFICER', 'ACCOUNTS', 'CEO', 'ADMIN', 'GROUND_OPS'];

export const ScheduledSopTab: React.FC<ScheduledSopTabProps> = ({ settings, setSettings, saveSettings }) => {
  const sop = settings.scheduledSop || {
    enabled: true,
    operatorCanDecline: false,
    requireCommandApprovalForDecline: true,
    declineRoles: ['DFO', 'ADMIN', 'CEO'] as Role[],
    commandChainRoles: ['RESERVATIONS', 'DFO', 'ADMIN'] as Role[],
    targetResponseMinutes: 15,
    initialResponseTemplate: '',
    pendingApprovalTemplate: '',
  };

  const updateSop = (patch: Partial<typeof sop>) => {
    const next = {
      ...settings,
      scheduledSop: {
        ...sop,
        ...patch,
      },
    } as AdminSettings;
    setSettings(next);
    setTimeout(() => saveSettings(next), 800);
  };

  const parseRoleCsv = (value: string): Role[] =>
    String(value || '')
      .split(',')
      .map(v => v.trim().toUpperCase())
      .filter((v): v is Role => ROLE_OPTIONS.includes(v as Role));

  return (
    <div className="space-y-8 animate-in slide-in-from-bottom-4">
      <section className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-10">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pb-8 border-b border-slate-100">
          <div>
            <h3 className="text-sm font-black text-slate-800 uppercase tracking-[0.2em]">Scheduled SOP</h3>
            <p className="text-sm text-slate-500 mt-3">
              Define command-chain policy for scheduled requests and control who can decline a client request.
            </p>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 bg-slate-50">
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${sop.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`} />
            <span className="text-xs font-bold tracking-[0.14em] uppercase text-slate-700">
              {sop.enabled ? 'SOP Active' : 'SOP Disabled'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-8">
          <div className="space-y-7">
            <PolicyToggle
              label="Enable Scheduled SOP"
              checked={!!sop.enabled}
              onChange={checked => updateSop({ enabled: checked })}
            />
            <PolicyToggle
              label="Operators Can Decline"
              checked={!!sop.operatorCanDecline}
              onChange={checked => updateSop({ operatorCanDecline: checked })}
            />
            <PolicyToggle
              label="Require Command Approval For Decline"
              checked={sop.requireCommandApprovalForDecline !== false}
              onChange={checked => updateSop({ requireCommandApprovalForDecline: checked })}
            />
            <AdminField
              label="Target Response Minutes"
              type="number"
              value={String(sop.targetResponseMinutes || 15)}
              onChange={v => updateSop({ targetResponseMinutes: Math.max(1, Number(v || 15) || 15) })}
            />
            <AdminField
              label="Decline Roles (comma)"
              type="text"
              value={(sop.declineRoles || []).join(', ')}
              onChange={v => updateSop({ declineRoles: parseRoleCsv(v) })}
            />
            <AdminField
              label="Command Chain Roles (comma)"
              type="text"
              value={(sop.commandChainRoles || []).join(', ')}
              onChange={v => updateSop({ commandChainRoles: parseRoleCsv(v) })}
            />
            <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400">
              Valid roles: {ROLE_OPTIONS.join(', ')}
            </p>
          </div>

          <div className="space-y-6">
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Initial Response Template</label>
              <textarea
                rows={5}
                value={sop.initialResponseTemplate || ''}
                onChange={e => updateSop({ initialResponseTemplate: e.target.value })}
                className="w-full bg-slate-50 border-none rounded-2xl p-4 font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500/20 outline-none mt-2"
              />
            </div>
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Pending Approval Template</label>
              <textarea
                rows={5}
                value={sop.pendingApprovalTemplate || ''}
                onChange={e => updateSop({ pendingApprovalTemplate: e.target.value })}
                className="w-full bg-slate-50 border-none rounded-2xl p-4 font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500/20 outline-none mt-2"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

